import Highcharts, { YAxisOptions } from "highcharts";

import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { chain } from "@/utils/chain";
import { YAxisAdapter } from "@/utils/chart/adapters/axis/y-axis-adapter";
import { ColumnSeriesAdapter } from "@/utils/chart/adapters/series/column-series-adapter";
import {
  IColumnAxisAdapter,
  ICategorizedSerieAdapter,
} from "@/utils/chart/adapters/types/categorized-chart-adapter.type";

export interface IPlotOpts {
  format: string;
  prefixSymbol: string;
  unitSymbol: string;
}

export interface IColumnChartSpecs<KIND extends string> {
  seriesAdapter: ReadonlyArray<[KIND, ICategorizedSerieAdapter<KIND>]>;
  yAxisAdapter?: ReadonlyArray<[KIND, IColumnAxisAdapter<KIND>]>;
  plotOpts?: Partial<IPlotOpts>;
  accessibilityDescription?: string;
}

export function buildColumnChartOptions<KIND extends string>(
  specs: IColumnChartSpecs<KIND>,
  series: TCategorizedDataResponseSerie<KIND>[],
): Highcharts.Options {
  const unit = series[0]?.unit ?? ""; // unit should be the same for all column series

  const yAxisOpts: YAxisOptions[] = chain(series)
    .uniqBy((serie) => serie.unit)
    .flatMap((serie) => {
      const kind = serie.kind;
      const maybeAdapterTuple = specs.yAxisAdapter?.find(([k]) => k === kind);
      const adapter = maybeAdapterTuple?.[1] ?? YAxisAdapter();

      return adapter.adapt(serie);
    })
    .value();

  const seriesOpts: Highcharts.SeriesOptionsType[] = series.flatMap((serie) => {
    const kind = serie.kind;
    const maybeAdapterTuple = specs.seriesAdapter.find(([k]) => k === kind);
    const adapter = maybeAdapterTuple ? maybeAdapterTuple[1] : ColumnSeriesAdapter();

    return adapter.adapt(serie);
  });

  return {
    chart: {
      type: "column",
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "category",
      crosshair: true,
    },
    yAxis: yAxisOpts,
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          crop: true,
          format:
            specs.plotOpts?.format ??
            `{point.y} ${specs.plotOpts?.prefixSymbol ?? ""}${specs.plotOpts?.unitSymbol ?? unit}`,
        },
        borderRadius: 15,
      },
    },
    credits: {
      enabled: false,
    },
    series: seriesOpts,
    accessibility: {
      description: specs.accessibilityDescription,
    },
  };
}
